import { useCallback } from 'react';
import { graphql, useMutation, useRelayEnvironment } from 'react-relay';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import type {
	IssueToCreate,
	MutationError,
	ContextualFields,
} from '@atlassian/jira-issue-table-inline-issue-create/src/common/types.tsx';
import { JiraIssueAri } from '@atlassian/ari/jira';
import { useIssueCreateOnError } from '@atlassian/jira-issue-table-inline-issue-create/src/controllers/use-issue-create-on-error/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import type { GroupContextField } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/types.tsx';
import { useShouldSkipIssuesConnectionUpdate } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/use-should-skip-issues-connection-update/index.tsx';
import { useQueuedMutationCommit } from '@atlassian/jira-issue-table-inline-issue-create/src/controllers/use-queued-mutation-commit/index.tsx';
import { useSortContext } from '../../controllers/sort-change-context-provider/index.tsx';
import { useEventHandlers } from '../../controllers/event-handlers-context/index.tsx';
import { ANALYTICS_SOURCE, INLINE_ISSUE_CREATE_EXPERIENCE } from '../../common/constants.tsx';
import {
	useIsDensityFull,
	useIsInlineEditingEnabled,
	useIsIssueHierarchyEnabled,
	useProjectContext,
	useIsIssueRankingEnabled,
	useIsConditionalFormattingEnabled,
	useIsTimelineEnabled,
	useIsRelationshipFieldEnabled,
	useIsEditableSyncLoaded,
	useIsSkipIssueCreateSuccessFlagEnabled,
} from '../../controllers/features/selectors.tsx';
import {
	issueConnectionUpdater,
	type IssueInsertionArgs,
	removeHighlightAfterDelay,
} from '../../controllers/issue-connection-updater/index.tsx';
import { useIssueFetchQueryVariables } from '../../controllers/issue-fetch-query-variables/index.tsx';
import { useIssueFetchAndAppend } from '../issue-fetch-and-append/useIssueFetchAndAppend.tsx';
import type { RankFieldTerms } from '../../common/types.tsx';
import type {
	CreateIssueControllerMutation,
	CreateIssueControllerMutation$data as CreateIssueMutationResponse,
} from './__generated__/CreateIssueControllerMutation.graphql';
import { toIssueCreateInput } from './utils/to-issue-create-input.tsx';
import { toOptimisticResponse } from './utils/to-optimistic-response.tsx';
import { useOptimisticId } from './utils/use-optimistic-id.tsx';

const ACTION_SUBJECT_ID = 'issueTableIssueCreate';
const PACKAGE_LOCATION = 'jira-native-issue-table-inline-issue-create';

const issueConnectionMutationUpdater = (
	store: RecordSourceSelectorProxy<CreateIssueMutationResponse>,
	response: CreateIssueMutationResponse | null | undefined,
	issueConnectionId: string,
	insertionArgs?: IssueInsertionArgs,
	parentItemConnectionId?: string,
	projectKey?: string | null,
	groupId?: string,
) => {
	const issue = response?.jira?.createIssue?.issue;
	const issueId = issue?.__id; // it's always defined, but relay types it as nullable, relay sets this field whether it's there or not.
	if (!issue || !issueId) {
		return;
	}
	issueConnectionUpdater(store, {
		...insertionArgs,
		issue,
		issueConnectionId,
		parentItemConnectionId,
		issueRecordId: issueId,
		fieldSetsConnectionId: issue.fieldSetsById?.__id ?? '',
		projectKey,
		groupId,
	});
};

/**
 * Function called during inline issue create to submit a newly created issue.
 *
 * @param issueToCreate Issue object provided during inline issue create.
 * @param additionalFields Additional field value data that can be provided for issue create.
 * @param insertionArgs Arguments to determine where the created issue should be inserted in the connection.
 */
type IssueCreateSubmitFunction = (
	issueToCreate: IssueToCreate,
	contextualFields: ContextualFields,
	insertionArgs?: IssueInsertionArgs,
) => void;

/**
 * Returns a function to create a new issue in the issue table with data provided during inline issue create.
 *
 * @param issueConnectionId ID of the Relay connection where the new issue should be appended.
 * @param parentItemConnectionId The ID of the parent issue in the Relay connection, used to update the parent issue in the Relay store after creating a child issue.
 */
export const useIssueCreateMutation = ({
	issueConnectionId,
	parentItemConnectionId,
	groupContextFieldInput,
	jql,
	location,
	rankFieldTerms,
}: {
	issueConnectionId: string | null;
	parentItemConnectionId?: string;
	groupContextFieldInput?: GroupContextField;
	jql?: string | null;
	location?: string;
	rankFieldTerms?: RankFieldTerms;
}): IssueCreateSubmitFunction => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const environment = useRelayEnvironment();
	const handleErrorIIC = useIssueCreateOnError();
	const shouldSkipIssuesConnectionUpdateAndShowFlag = useShouldSkipIssuesConnectionUpdate();
	const isLinkRelationshipFieldEnabled = useIsRelationshipFieldEnabled();
	const isEditableSyncLoaded = useIsEditableSyncLoaded();
	const isSkipIssueCreateSuccessFlagEnabled = useIsSkipIssueCreateSuccessFlagEnabled();

	const { onSortChange } = useSortContext();
	const { onIssueCreated } = useEventHandlers();

	const { fieldSetIds } = useIssueFetchQueryVariables();
	const onIssueFetchAndAppend = useIssueFetchAndAppend({
		issueConnectionId,
		groupContextFieldInput,
		jql,
	});

	const { generateOptimisticId, getRealId } = useOptimisticId();

	let queuedCommit:
		| ReturnType<typeof useQueuedMutationCommit<CreateIssueControllerMutation>>
		| undefined;
	if (fg('jsc_list_reranking_gate')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const generateDeferredVariables = useCallback(
			(oldVariables: CreateIssueControllerMutation['variables']) => {
				const rank = oldVariables.issueCreateInput.rank;
				if (rank) {
					return {
						...oldVariables,
						issueCreateInput: {
							...oldVariables.issueCreateInput,
							rank: {
								...rank,
								afterIssueId: rank.afterIssueId
									? (getRealId(rank.afterIssueId) ?? rank.afterIssueId)
									: rank.afterIssueId,
								beforeIssueId: rank.beforeIssueId
									? (getRealId(rank.beforeIssueId) ?? rank.beforeIssueId)
									: rank.beforeIssueId,
							},
						},
					};
				}
				return oldVariables;
			},
			[getRealId],
		);

		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		queuedCommit = useQueuedMutationCommit<CreateIssueControllerMutation>({
			generateDeferredVariables,
		});
	}
	const [commit] = useMutation<CreateIssueControllerMutation>(
		graphql`
			mutation CreateIssueControllerMutation(
				$issueCreateInput: JiraIssueCreateInput!
				$fieldSetIds: [String!]!
				$isInlineEditingEnabled: Boolean!
				$isIssueHierarchyEnabled: Boolean!
				$shouldQueryFieldsByIdOrAlias: Boolean!
				$fieldIdsOrAliases: [String!]!
				$isDensityFull: Boolean!
				$projectKey: String
				$issueSearchInput: JiraIssueSearchInput!
				$shouldQueryMatchesIssueSearch: Boolean!
				$isConditionalFormattingEnabled: Boolean!
				$isIdFieldEnabled: Boolean!
				$isTimelineEnabled: Boolean!
				$isLinkRelationshipFieldEnabled: Boolean!
				$isEditableSyncLoaded: Boolean!
			) @raw_response_type {
				jira {
					createIssue(input: $issueCreateInput) @optIn(to: "JiraIssueCreateMutation") {
						errors {
							extensions {
								statusCode
							}
						}
						issue {
							...highlightIssueRow
							__id @include(if: $isIdFieldEnabled)
							id
							key
							issueId
							issueTypeField @include(if: $isIssueHierarchyEnabled) {
								id
								issueType {
									hierarchy {
										level
									}
								}
							}
							fieldsByIdOrAlias(idsOrAliases: $fieldIdsOrAliases)
								@optIn(to: "JiraIssueFieldsByIdOrAlias")
								@include(if: $shouldQueryFieldsByIdOrAlias) {
								__typename
								fieldId
								... on JiraStatusField {
									status {
										statusId
										name
									}
								}
								... on JiraPriorityField {
									priority {
										priorityId
									}
								}
								... on JiraSingleSelectUserPickerField {
									user {
										accountId
									}
								}
								... on JiraSingleSelectField {
									fieldOption {
										optionId
									}
								}
								... on JiraSprintField {
									selectedSprintsConnection(first: 1) {
										edges {
											node {
												sprintId
											}
										}
									}
								}
								... on JiraNumberField {
									number
								}
								... on JiraLabelsField {
									selectedLabelsConnection {
										edges {
											node {
												name
											}
										}
									}
								}
								... on JiraRadioSelectField {
									selectedOption {
										optionId
									}
								}
								... on JiraMultipleSelectField {
									selectedOptions {
										edges {
											node {
												optionId
											}
										}
									}
								}
								... on JiraCheckboxesField {
									selectedOptions {
										edges {
											node {
												optionId
											}
										}
									}
								}
								... on JiraMultipleVersionPickerField {
									selectedVersionsConnection {
										edges {
											node {
												versionId
											}
										}
									}
								}
								... on JiraSingleVersionPickerField {
									version {
										versionId
									}
								}
							}
							matchesIssueSearch(issueSearchInput: $issueSearchInput)
								@include(if: $shouldQueryMatchesIssueSearch)
							canHaveChildIssues(projectKey: $projectKey) @include(if: $isIssueHierarchyEnabled)
							fieldSetsById(fieldSetIds: $fieldSetIds, first: 500) {
								__id @include(if: $isIdFieldEnabled)
								...IssueRow_fieldSets
									@arguments(
										isInlineEditingEnabled: $isInlineEditingEnabled
										isDensityFull: $isDensityFull
										isConditionalFormattingEnabled: $isConditionalFormattingEnabled
										isTimelineEnabled: $isTimelineEnabled
										isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
										isEditableSyncLoaded: $isEditableSyncLoaded
									)
							}
						}
					}
				}
			}
		`,
		queuedCommit,
	);

	const projectKey = useProjectContext();

	const isDensityFull = useIsDensityFull();
	const isTimelineEnabled = useIsTimelineEnabled();
	const isInlineEditingEnabled = useIsInlineEditingEnabled();
	const isIssueHierarchyEnabled = useIsIssueHierarchyEnabled();

	const onExperienceStart = useExperienceStart({
		experience: INLINE_ISSUE_CREATE_EXPERIENCE,
		analyticsSource: ANALYTICS_SOURCE,
	});

	const onExperienceFail = useExperienceFail({
		experience: INLINE_ISSUE_CREATE_EXPERIENCE,
		attributes: {
			isGroupIssueCreate: !!groupContextFieldInput,
		},
	});

	const onExperienceSuccess = useExperienceSuccess({
		experience: INLINE_ISSUE_CREATE_EXPERIENCE,
	});

	const isIssueRankingEnabled = useIsIssueRankingEnabled();

	const isConditionalFormattingEnabled =
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		fg('jira_list_conditional_formatting') && useIsConditionalFormattingEnabled();

	return useCallback(
		(
			issueToCreate: IssueToCreate,
			contextualFields: ContextualFields,
			insertionArgs?: IssueInsertionArgs,
		) => {
			const issueCreateInput = toIssueCreateInput(
				issueToCreate,
				contextualFields,
				groupContextFieldInput,
				isIssueRankingEnabled,
			);
			if (!issueCreateInput || issueConnectionId == null) {
				return;
			}
			const insertionArgsWithIdGetter =
				insertionArgs == null
					? insertionArgs
					: {
							...insertionArgs,
							getRealId,
						};

			const experienceExtraAttributes =
				location && fg('jira_timeline_analytics_harmonization_v2')
					? {
							actionLocation: location,
						}
					: undefined;

			const handleError = async (error: Error | ReadonlyArray<MutationError>) => {
				const response = await handleErrorIIC({
					issueToCreate,
					contextualFields,
					groupContextFieldInput,
					error,
					onFail: (e) => onExperienceFail(PACKAGE_LOCATION, e, experienceExtraAttributes),
				});
				if (response) {
					onIssueFetchAndAppend(response, insertionArgsWithIdGetter);
					onExperienceSuccess(experienceExtraAttributes);
				}
			};

			const { id: optimisticId, saveRealId } = generateOptimisticId();
			onExperienceStart();

			commit({
				variables: {
					issueCreateInput,
					fieldSetIds,
					isInlineEditingEnabled,
					isDensityFull,
					isIssueHierarchyEnabled,
					isIdFieldEnabled: !fg('nin-optimistic-flashing-fix'),
					isConditionalFormattingEnabled,
					isTimelineEnabled,
					isLinkRelationshipFieldEnabled,
					isEditableSyncLoaded,
					projectKey,
					...(groupContextFieldInput
						? {
								shouldQueryFieldsByIdOrAlias: true,
								fieldIdsOrAliases: [groupContextFieldInput.fieldId],
							}
						: {
								shouldQueryFieldsByIdOrAlias: false,
								fieldIdsOrAliases: [],
							}),
					...(jql
						? {
								shouldQueryMatchesIssueSearch: true,
								issueSearchInput: { jql },
							}
						: {
								shouldQueryMatchesIssueSearch: false,
								issueSearchInput: { jql: '' },
							}),
				},
				optimisticResponse: toOptimisticResponse(
					issueToCreate,
					optimisticId,
					isIssueHierarchyEnabled,
					groupContextFieldInput,
				),
				optimisticUpdater: (store, response) =>
					issueConnectionMutationUpdater(
						store,
						response,
						issueConnectionId,
						insertionArgsWithIdGetter,
						parentItemConnectionId,
						projectKey,
					),
				updater: (store, response) => {
					if (response != null && response?.jira?.createIssue?.issue?.id != null) {
						saveRealId(response?.jira?.createIssue?.issue?.id);
					}
					if (
						shouldSkipIssuesConnectionUpdateAndShowFlag(
							groupContextFieldInput,
							response?.jira?.createIssue?.issue,
							isIssueRankingEnabled,
							onSortChange,
							rankFieldTerms,
							isSkipIssueCreateSuccessFlagEnabled,
						)
					) {
						return;
					}
					return issueConnectionMutationUpdater(
						store,
						response,
						issueConnectionId,
						insertionArgsWithIdGetter,
						parentItemConnectionId,
						projectKey,
						groupContextFieldInput?.groupId,
					);
				},
				onCompleted: (response) => {
					const { errors, issue } = response?.jira?.createIssue ?? {};
					if (!issue) {
						// Handle any GraphQL errors that occurred during issue create
						errors && handleError(errors);
						return;
					}
					// Event contributes towards Jira Monthly Core User, see https://hello.atlassian.net/wiki/spaces/ANALYTICS/pages/3767029088/Monthly+Core+User+MCU+Definition#%3Ajira%3A-Jira.
					fireTrackAnalytics(createAnalyticsEvent({}), 'issue created', ACTION_SUBJECT_ID, {
						...(location && fg('jsc_list_reranking_gate') && { location }),
						...(fg('jira_render_pic_in_nin') && {
							fields: {
								hasAssignee: Boolean(issueToCreate.assignee),
								hasDueDate: Boolean(issueToCreate.dueDate),
							},
						}),
						...(fg('jira_timeline_analytics_harmonization_v2') && {
							issueHierarchy: issueToCreate.issueType.hierarchy?.level,
							parentId: contextualFields.parentIssueAri
								? JiraIssueAri.parse(contextualFields.parentIssueAri).issueId
								: undefined,
							issueType: issueToCreate.issueType.name,
							location,
						}),
					});
					onExperienceSuccess(experienceExtraAttributes);
					removeHighlightAfterDelay(environment, issue);

					if (onIssueCreated && fg('timeline_rollups_issue_table_callbacks')) {
						onIssueCreated(issue.issueId);
					}
				},
				onError: (error) => {
					// Handle any network errors that occurred during issue create
					handleError(error);
				},
			});
		},
		[
			groupContextFieldInput,
			isIssueRankingEnabled,
			issueConnectionId,
			getRealId,
			generateOptimisticId,
			onExperienceStart,
			commit,
			fieldSetIds,
			isInlineEditingEnabled,
			isDensityFull,
			isIssueHierarchyEnabled,
			isConditionalFormattingEnabled,
			projectKey,
			jql,
			handleErrorIIC,
			onExperienceFail,
			onIssueFetchAndAppend,
			onExperienceSuccess,
			parentItemConnectionId,
			shouldSkipIssuesConnectionUpdateAndShowFlag,
			onSortChange,
			rankFieldTerms,
			createAnalyticsEvent,
			location,
			environment,
			isTimelineEnabled,
			isLinkRelationshipFieldEnabled,
			isEditableSyncLoaded,
			isSkipIssueCreateSuccessFlagEnabled,
			onIssueCreated,
		],
	);
};

export const CreateIssueController = ({
	childConnectionId,
	parentItemConnectionId,
	groupContextFieldInput,
	setOnCreateIssue,
	jql,
	location,
	rankFieldTerms,
}: {
	childConnectionId: string;
	parentItemConnectionId?: string;
	groupContextFieldInput?: GroupContextField;
	jql?: string | null;
	location?: string;
	rankFieldTerms?: RankFieldTerms;
	setOnCreateIssue: (onCreateIssue: IssueCreateSubmitFunction) => void;
}) => {
	const onCreateIssue = useIssueCreateMutation({
		issueConnectionId: childConnectionId,
		parentItemConnectionId,
		groupContextFieldInput,
		jql,
		...(fg('jsc_list_reranking_gate') && { location, rankFieldTerms }),
	});
	setOnCreateIssue(onCreateIssue);

	return null;
};
